// newsData.js
import cd38Img1 from '../assets/news/20250425/1.png';
import cd38Img2 from '../assets/news/20250425/2.png';
import cd38Img3 from '../assets/news/20250425/3.png';
import cd38Img4 from '../assets/news/20250425/4.png';
import cd38Img5 from '../assets/news/20250425/5.png';
import cd38Img6 from '../assets/news/20250425/6.png';
import nlrp3Title from '../assets/news/20260929/title.jpg';
import nlrp3Interface from '../assets/news/20260929/interface.jpg';
import nlrp3Platform from '../assets/news/20260929/platform.jpg';
import nlrp3Conclusion from '../assets/news/20260929/conclusion.jpg';

export const newsData = [
  {
    id: 1,
    slug: "2025-04-25",
    date: "April 2026",
    publishDate: "2026-04-25", // For sorting
    tag: "Press Release",
    images: [cd38Img1, cd38Img2, cd38Img3, cd38Img4, cd38Img5, cd38Img6],
    title: "Genion Quantum Announces Breakthrough AI-Driven Discovery of Potent CD38 Inhibitors",
    location: "Rockville, MD — April 2026 — Genion Quantum Technologies Inc.",
    summary: "Genion Quantum Technologies Inc. today announced a significant scientific advancement in AI-powered drug discovery, demonstrating the rapid identification of potent CD38 inhibitors using its proprietary AI- and structure-integrated molecular design platform.",
    lead: [
      "Genion Quantum Technologies Inc. today announced a significant scientific advancement in AI-powered drug discovery, demonstrating the rapid identification of potent CD38 inhibitors using its proprietary AI- and structure-integrated molecular design platform.",
      "The study, conducted in collaboration with researchers from the University of Texas at Austin and the National Institute of Mental Health (NIH), showcases a next-generation approach that combines artificial intelligence with high-resolution structural biology to accelerate therapeutic discovery."
    ],
    sections: [
      {
        title: "A New Paradigm in Drug Discovery",
        icon: "TrendingUp",
        content: [
          "CD38 is a clinically validated target implicated in cancer, metabolic disorders, inflammation, and neurodegenerative diseases. However, discovering effective inhibitors has traditionally been slow and limited by conventional screening approaches.",
          "Genion Quantum’s platform introduces a closed-loop, AI-driven discovery system that integrates graph-based AI models, structure-based molecular docking, and iterative feedback from binding-mode analysis."
        ],
        list: [
          "Graph-based AI models trained on experimental bioactivity data",
          "Structure-based molecular docking and simulation",
          "Iterative feedback from binding-mode analysis",
          "Quantitative prediction of drug potency"
        ]
      },
      {
        title: "Key Scientific Highlights",
        icon: "Science",
        isHighlight: true,
        items: [
          { primary: "Large-scale screening", secondary: "Over 1.5 million compounds analyzed using AI models." },
          { primary: "High predictive accuracy", secondary: "Strong correlation between predicted and experimental activity (R² = 0.72)." },
          { primary: "Ultra-potent candidates identified", secondary: "Top inhibitor with IC50 ≈ 0.3 nM (experimental) matching AI prediction at 0.4 nM." },
          { primary: "Novel molecule generation", secondary: "AI-designed compounds achieved ≤ 4 nM predicted potency." },
          { primary: "Optimized hybrid molecules", secondary: "AI-guided design improved activity to ~0.2 nM, surpassing known reference compounds." }
        ],
        footer: "Importantly, the system identified key structural motifs (e.g., imidazole, pyridazinone) and critical binding interactions with CD38 residues such as W125, W189, and T221, enabling rational optimization."
      },
      {
        title: "Platform Vision: AI Molecular Reasoning Engine",
        icon: "Psychology",
        content: [
          "Unlike conventional AI drug discovery methods that rely purely on statistical correlations, Genion Quantum’s platform embeds structural constraints directly into AI models and uses binding-mode feedback to refine molecular generation.",
          "This results in higher-quality candidates and significantly reduces false positives."
        ],
        list: [
          "Embeds structural constraints directly into AI models",
          "Uses binding-mode feedback to refine molecular generation",
          "Ensures chemical realism, synthetic feasibility, and biological relevance"
        ]
      }
    ],
    impact: {
      title: "Broad Impact Beyond CD38",
      icon: "Public",
      content: "While this study focuses on CD38 inhibitors, the underlying framework is generalizable across therapeutic targets, including:",
      list: ["Oncology", "Immunology", "Neurodegenerative diseases", "Metabolic disorders"],
      footer: "The modular architecture enables rapid adaptation to new targets, supporting next-generation precision medicine development."
    }
  },
  {
    id: 2,
    slug: "2026-09-29",
    date: "September 2026",
    publishDate: "2026-09-29", // For sorting
    tag: "Research Update",
    images: [nlrp3Title, nlrp3Interface, nlrp3Platform, nlrp3Conclusion],
    heroImage: {
      src: nlrp3Title,
      alt: "Targeting the NLRP3–NEK7 Interface: a joint program in structure-based virtual screening and LLM-driven lead optimization"
    },
    title: "Targeting the NLRP3–NEK7 Interface: AI-Driven Discovery of Molecular Wedge Inhibitors",
    location: "Rockville, MD — September 2026 — Genion Quantum Technologies Inc.",
    summary: "Genion Quantum Technologies Inc. today reported the results of a joint program in structure-based virtual screening and LLM-driven lead optimization, identifying 70 structurally diverse molecular wedge candidates designed to disrupt the NLRP3–NEK7 protein–protein interaction.",
    lead: [
      "Genion Quantum Technologies Inc. today reported the results of a joint program in structure-based virtual screening and LLM-driven lead optimization, identifying 70 structurally diverse molecular wedge candidates designed to disrupt the NLRP3–NEK7 protein–protein interaction.",
      "The NLRP3 inflammasome is a central regulator of innate immune signaling and a compelling therapeutic target across inflammatory, autoimmune, cardiovascular, and neurodegenerative diseases. Its activation depends on recruitment of NEK7, an essential licensing step for inflammasome assembly."
    ],
    sections: [
      {
        title: "A Molecular Wedge Strategy",
        icon: "TrendingUp",
        content: [
          "Disrupting the NLRP3–NEK7 interaction may selectively suppress inflammasome activation while limiting interference with other physiological signaling pathways.",
          "Unlike ATP-competitive kinase inhibitors or conventional orthosteric inhibitors, molecular wedge inhibitors are designed to occupy critical pockets at the protein interface and physically prevent complex formation."
        ],
        image: {
          src: nlrp3Interface,
          alt: "Cryo-EM structure of NEK7 bound to NLRP3, with zoomed views of the NACHT and LRR interfaces and key interacting residues",
          caption: "NEK7 (green) binds in a groove formed by the NACHT and LRR domains of NLRP3 (blue). This bridging contact is the interface the wedge inhibitors are designed to block."
        }
      },
      {
        title: "AI-Powered Discovery Platform",
        icon: "Psychology",
        content: [
          "The program applies Genion Quantum’s proprietary AI-driven drug discovery platform in a closed design-make-test-analyze cycle."
        ],
        list: [
          "Ultra-large virtual screening using AI generative models and proprietary chemical space",
          "LLM-orchestrated, multi-objective optimization balancing potency, selectivity, ADMET, and synthetic feasibility",
          "Physics-based validation with docking, molecular dynamics, and free-energy (FEP) calculations",
          "Medicinal chemistry oversight to ensure synthetic tractability and drug-like properties"
        ],
        image: {
          src: nlrp3Platform,
          alt: "Genion Quantum's AI platform: ultra-large virtual screening, LLM-orchestrated DMTA optimization, and physics-based validation",
          caption: "Integrated AI + physics + expert insight: the three pillars of the discovery engine."
        }
      },
      {
        title: "Conclusion: From One Million Structures to 70 Candidates",
        icon: "Science",
        isHighlight: true,
        items: [
          { primary: ">1,000,000 structures screened", secondary: "Large-scale virtual screening against the NLRP3–NEK7 interface." },
          { primary: "~10,000 docking hits", secondary: "Compounds with favorable predicted docking at the interface." },
          { primary: "~1,000 high-confidence binders", secondary: "Hits refined by hybrid AI and structure-based scoring." },
          { primary: "~200 free-energy-prioritized molecules", secondary: "Ranked by physics-based binding free-energy evaluation." },
          { primary: "70 structurally diverse candidates", secondary: "Selected through scaffold clustering and structural-diversity analysis." }
        ],
        footer: "The 70 candidates show favorable predicted interface binding, binding free energies, and drug-like properties.",
        image: {
          src: nlrp3Conclusion,
          alt: "Screening funnel from more than 1,000,000 structures to 70 structurally diverse candidates, with a candidate molecule docked at the NLRP3–NEK7 interface",
          caption: "The screening funnel, from over one million structures to 70 high-confidence candidates."
        }
      },
      {
        title: "Next Step: Experimental Validation",
        icon: "Bolt",
        content: [
          "The 70 candidates now provide a focused starting set for experimental PPI disruption and inflammasome assays, followed by iterative lead optimization.",
          "Based on prior experience, an estimated 5–10% of candidates (about 4–7 compounds) may show assay activity. This is a projection, not an experimental result."
        ]
      }
    ]
  }
];
